import styled from '@emotion/styled'
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { DownOutlined, PictureOutlined } from '@ant-design/icons'
import { useFormikContext } from 'formik'
import type {
  DivisionDraft,
  WizardFormValues,
} from '@sportninja/common/features/competition/wizard'
import DivisionRow from '../components/DivisionRow'
import InlineCreateRow from '../components/InlineCreateRow'
import { competitionWord } from '@sportninja/common/features/competition/wizard'
import Text from 'src/components/ts-components/Text'
import colors from '@sportninja/common/constants/appColors'

type Props = { competitionName: string }

const makeClientId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `c-${Math.random().toString(36).slice(2, 11)}`

const Step4DivisionsSubStep = ({ competitionName }: Props) => {
  const { values, setFieldValue } = useFormikContext<WizardFormValues>()
  const divisions = values.divisions
  const sensors = useSensors(useSensor(PointerSensor))

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) {
      return
    }
    const oldIndex = divisions.findIndex((d) => d.clientId === active.id)
    const newIndex = divisions.findIndex((d) => d.clientId === over.id)
    if (oldIndex < 0 || newIndex < 0) {
      return
    }
    setFieldValue('divisions', arrayMove(divisions, oldIndex, newIndex))
  }

  const addDivision = (name: string) => {
    const next: DivisionDraft = {
      clientId: makeClientId(),
      name,
      order: divisions.length,
    }
    setFieldValue('divisions', [...divisions, next])
  }

  const renameDivision = (clientId: string, name: string) => {
    setFieldValue(
      'divisions',
      divisions.map((d) => (d.clientId === clientId ? { ...d, name } : d))
    )
  }

  const deleteDivision = (clientId: string) => {
    setFieldValue(
      'divisions',
      divisions.filter((d) => d.clientId !== clientId)
    )
  }

  return (
    <Container>
      <Header>
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          Create Divisions
        </Text>
        <Text variant='paragraphMedium' color={colors.NEUTRAL_50}>
          Set up team groupings for the {competitionWord({ capitalize: false })}{' '}
          tables.
        </Text>
      </Header>
      <Tree>
        <CompetitionRow>
          <ChevronWrap>
            <DownOutlined />
          </ChevronWrap>
          <CompetitionIcon>
            <PictureOutlined />
          </CompetitionIcon>
          <CompetitionText>
            <Text variant='paragraphMedium' weight='bold' color={colors.WHITE}>
              {competitionName || competitionWord()}
            </Text>
            <Text variant='paragraphXSmall' color={colors.NEUTRAL_50}>
              {competitionWord()}
            </Text>
          </CompetitionText>
        </CompetitionRow>
        <Children>
          <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <SortableContext
              items={divisions.map((d) => d.clientId)}
              strategy={verticalListSortingStrategy}
            >
              {divisions.map((d) => (
                <ChildItem key={d.clientId}>
                  <DivisionRow
                    clientId={d.clientId}
                    name={d.name}
                    onRename={(name) => renameDivision(d.clientId, name)}
                    onDelete={() => deleteDivision(d.clientId)}
                  />
                </ChildItem>
              ))}
            </SortableContext>
          </DndContext>
          <ChildItem $compact>
            <InlineCreateRow
              label='Add Division'
              placeholder='Division name'
              onCreate={addDivision}
            />
          </ChildItem>
        </Children>
      </Tree>
    </Container>
  )
}

export default Step4DivisionsSubStep

// ---- styled ----
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`

const Tree = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 720px;
`

const CompetitionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: ${colors.SECONDARY_500};
  border-radius: 8px;
`

const ChevronWrap = styled.span`
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${colors.NEUTRAL_50};
`

const CompetitionIcon = styled.span`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${colors.PRIMARY};
  color: ${colors.BLACK};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`

const CompetitionText = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

const Children = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0 0 36px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 20px;
    top: 0;
    height: calc(100% - 15px);
    width: 1px;
    background: ${colors.SECONDARY_300};
  }
`

const ChildItem = styled.div<{ $compact?: boolean }>`
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: -16px;
    top: ${(p) => (p.$compact ? '19px' : '26px')};
    width: 16px;
    height: 1px;
    background: ${colors.SECONDARY_300};
  }
`
